import { PoseConfig } from 'lib/interfaces/StepsConfig';
import {
  SelfieCaptureOption,
  SelfieMatch,
  VerificationConfigSelfie,
} from 'lib/interfaces/verificationConfig';
import {
  CapturedSelfie,
  SelfiePoseOrder,
  SelfieStep,
  SelfieThreePointPoseOrder,
} from 'verify/lib/interfaces/selfie';
import { Inquiry } from 'verify/models/inquiry';
import { CaptureMethod } from 'verify/models/verification';

export function getRequireSelfieMatch(inquiry: Inquiry): SelfieMatch {
  const selfieConfig = (inquiry.attributes['next-step'].config || {}) as VerificationConfigSelfie;

  return selfieConfig['require-selfie-match'] || SelfieMatch.ProfileAndCenter;
}

export function getRequireStrictSelfieCapture(inquiry: Inquiry): boolean {
  const selfieConfig = (inquiry.attributes['next-step'].config || {}) as VerificationConfigSelfie;

  return selfieConfig['require-strict-selfie-capture'] || false;
}

export function hasPose(capturedSelfies: CapturedSelfie[], pose: SelfieStep): boolean {
  return !!capturedSelfies.find((capturedSelfie) => capturedSelfie.pose === pose);
}

export function getCurrentSelfieType(
  capturedSelfies: CapturedSelfie[],
  requireSelfieMatch: SelfieMatch,
  orderedPoses?: SelfiePoseOrder,
): SelfieStep | undefined {
  if (orderedPoses && SelfieMatch.ConfigurablePoses) {
    return getCurrentSelfieStep(capturedSelfies, orderedPoses);
  }

  // Steps presented in the order they must be completed
  switch (requireSelfieMatch) {
    case SelfieMatch.ProfileAndCenter:
      if (!hasPose(capturedSelfies, SelfieStep.Center)) {
        return SelfieStep.Center;
      } else if (!hasPose(capturedSelfies, SelfieStep.Left)) {
        return SelfieStep.Left;
      } else if (!hasPose(capturedSelfies, SelfieStep.Right)) {
        return SelfieStep.Right;
      } else {
        return undefined;
      }
    case SelfieMatch.OnlyCenter:
      if (!hasPose(capturedSelfies, SelfieStep.Center)) {
        return SelfieStep.Center;
      } else {
        return undefined;
      }
  }

  return undefined;
}

/**
 * Construct what poses should be captured based on verification selfie type
 */
export function getSelfiePoseOrder({
  orderedPoses,
  requireSelfieMatch,
}: {
  orderedPoses?: SelfieThreePointPoseOrder;
  requireSelfieMatch: SelfieMatch;
}): SelfiePoseOrder {
  const lastPoses: [SelfieStep.Left, SelfieStep.Right] | [] =
    requireSelfieMatch === SelfieMatch.OnlyCenter ? [] : [SelfieStep.Left, SelfieStep.Right];

  return orderedPoses ?? [SelfieStep.Center, ...lastPoses];
}

/**
 * Get what the current step is based off what has already been captured and what the pose order is
 */
export function getCurrentSelfieStep(
  capturedSelfies: CapturedSelfie[],
  poseOrder: SelfiePoseOrder,
): SelfieStep | undefined {
  for (const pose of poseOrder) {
    if (!hasPose(capturedSelfies, pose)) {
      return pose;
    }
  }

  // If we get to this line, there no more poses to complete
  return undefined;
}

export function getSelfieStepConfig(
  selfieStep: SelfieStep,
  poseConfigs: PoseConfig[] = [],
): PoseConfig | undefined {
  return poseConfigs.find((config) => config.pose === selfieStep);
}

export function addCapturedSelfie(
  capturedSelfies: CapturedSelfie[],
  selfieStep: SelfieStep,
  imageDataUrl: string,
  captureMethod: CaptureMethod,
  capturedAt?: string,
) {
  return [
    ...capturedSelfies,
    {
      dataUrl: imageDataUrl,
      captureMethod,
      pose: selfieStep,
      capturedAt,
    },
  ];
}

export const getSupportedSelfieCaptureOptions = ({
  verificationConfigSelfie,
  isMobile,
  isWebGLIncompatible,
  hasCamera,
}: {
  verificationConfigSelfie: VerificationConfigSelfie;
  isMobile: boolean;
  isWebGLIncompatible: boolean;
  hasCamera: boolean;
}): SelfieCaptureOption[] => {
  const enabledCaptureOptions =
    verificationConfigSelfie[
      isMobile ? 'enabled-capture-options-mobile' : 'enabled-capture-options-desktop'
    ];
  const showWebCameraOption =
    hasCamera &&
    !isWebGLIncompatible &&
    enabledCaptureOptions?.includes(SelfieCaptureOption.WebCamera);
  return [
    {
      option: SelfieCaptureOption.WebCamera,
      condition: showWebCameraOption,
    },
    // The `manual` option is a special option on top of `web_camera` to allow one to manually
    // capture a picture with a button if auto face detection is not working.
    {
      option: SelfieCaptureOption.Manual,
      condition: showWebCameraOption,
    },
    {
      option: SelfieCaptureOption.Stillfie,
      condition: !showWebCameraOption,
    },
    {
      option: SelfieCaptureOption.DeviceHandoff,
      condition: true,
    },
  ]
    .filter((opt) => opt.condition && enabledCaptureOptions?.includes(opt.option))
    .map((opt) => opt.option);
};

export const getSelfiePose = (
  capturedSelfies: CapturedSelfie[],
  pose: SelfieStep,
): CapturedSelfie | undefined => {
  return capturedSelfies.find((selfie) => selfie.pose === pose);
};

export const selfiesCapturedViaUpload = (capturedSelfies: CapturedSelfie[]) => {
  if (!capturedSelfies) {
    return false;
  }
  const captureMethods = Object.values(capturedSelfies).map(
    (capturedSelfie) => capturedSelfie.captureMethod,
  );
  return captureMethods.indexOf(CaptureMethod.Upload) >= 0;
};
